/**
 * Module-level search-param write queue (10 ms batch).
 *
 * Sets AND removals share the queue, so the tick resolves their order before a single
 * navigation. Two navigations in the same tick would race — react-router keeps the last
 * one — and a removal whose navigate had not reached `window.location` yet used to be
 * written straight back by the next flush.
 *
 * Known gap: the queue is not route-aware. A flush that lands after a route change inside
 * the 10 ms window merges against the new location and writes its params there. Fixing it
 * needs a pathname stamp per entry, dropped at flush time.
 */

import type { NavigateOptions, SetURLSearchParams } from "react-router";

/** A `null` value means the batch removes that name. */
type QueueItem = [
  name: string,
  value: string | null,
  options: NavigateOptions | undefined,
];

export type DrainedSearchParamWrites = {
  params: Record<string, string>;
  removed: string[];
  options: NavigateOptions;
};

const FLUSH_DELAY_MS = 10 as const;

const QUEUE: QueueItem[] = [];

let timeoutId: ReturnType<typeof setTimeout> | null = null;
let readSetSearchParams: (() => SetURLSearchParams) | null = null;

export const enqueueSearchParamWrite = (
  paramName: string,
  value: string,
  options?: NavigateOptions
): void => {
  QUEUE.push([paramName, value, options]);
};

export const enqueueSearchParamRemovals = (
  names: readonly string[],
  options?: NavigateOptions
): void => {
  for (const name of names) {
    if (name) QUEUE.push([name, null, options]);
  }
};

/**
 * Last queued value for a name: `null` when the batch removes it, `undefined` when the
 * batch leaves it alone. Read this before `window.location`, which still shows the
 * address as it was before the pending batch.
 */
export const peekQueuedSearchParamValue = (
  paramName: string
): string | null | undefined => {
  for (let i = QUEUE.length - 1; i >= 0; i -= 1) {
    if (QUEUE[i][0] === paramName) return QUEUE[i][1];
  }
  return undefined;
};

/**
 * Collapses the queue to the last entry per name and resolves the single set of navigate
 * options the batch travels with: `replace` when at least one write asked for it and none
 * asked for a history entry (`replace: false`), `preventScrollReset` when any asked for
 * it, everything else shallow-merged in queue order.
 *
 * Writes that pass no options abstain. Most writers never think about history, and
 * letting that silence count as a vote for `push` would turn another control's deliberate
 * `replace` into a duplicate history entry.
 */
export const drainQueuedSearchParamWrites = (): DrainedSearchParamWrites => {
  const lastValueByName = new Map<string, string | null>();
  let merged: NavigateOptions = {};
  let replaceVoted = false;
  let pushVoted = false;
  let preventScrollReset = false;

  for (const [name, value, options] of QUEUE) {
    lastValueByName.set(name, value);

    merged = { ...merged, ...options };
    replaceVoted ||= options?.replace === true;
    pushVoted ||= options?.replace === false;
    preventScrollReset ||= options?.preventScrollReset === true;
  }

  QUEUE.length = 0;

  const params: Record<string, string> = {};
  const removed: string[] = [];

  for (const [name, value] of lastValueByName) {
    if (value === null) {
      removed.push(name);
    } else {
      params[name] = value;
    }
  }

  return {
    params,
    removed,
    options: { ...merged, replace: replaceVoted && !pushVoted, preventScrollReset },
  };
};

/**
 * Live window is the base: it already holds writes from the URL writers that bypass this
 * queue. `prev` only adds keys the window does not have yet (one of those writes still in
 * flight). Queued removals win over both — the batch decided that name is gone, even if
 * the address bar still shows it.
 */
export const mergeQueuedSearchParamWrites = (
  prev: URLSearchParams,
  { params, removed }: Pick<DrainedSearchParamWrites, "params" | "removed">,
  liveSearch: string = typeof window !== "undefined" ? window.location.search : ""
): Record<string, string> => {
  const next = new URLSearchParams(liveSearch);
  const removedNames = new Set(removed);

  for (const [key, value] of prev.entries()) {
    if (!next.has(key) && !removedNames.has(key)) next.set(key, value);
  }
  for (const name of removedNames) {
    next.delete(name);
  }
  for (const [name, value] of Object.entries(params)) {
    next.set(name, value);
  }

  return Object.fromEntries(next.entries());
};

/**
 * One shared timer, so everything queued in the same window leaves in one navigation.
 * The window runs from the first queued write and is not extended by later ones —
 * rescheduling on every write lets a steady stream of updates starve the flush.
 *
 * The getter (rather than the setter itself) is what keeps the flush on the caller's
 * latest `setSearchParams`: react-router bakes the render's `searchParams` into it, and a
 * stale one hands the updater a `prev` frozen at mount.
 */
/**
 * Runs after the pending search-param flush. That flush navigates to `?search`
 * only (`useSearchParams` → `navigate("?" + params)`), and `createLocation`
 * defaults `hash` to `""`, so the navigation deletes `location.hash`.
 *
 * Hash writers that share a click with a search-param write (tab change +
 * `atomWithHash`) have to run here. A write before the flush is gone by the
 * time a newly mounted reader (`atomWithHash` `onMount`) loads the hash.
 * No flush pending → next macrotask.
 */
export const runAfterSearchParamsFlush = (cb: () => void): void => {
  const delay = timeoutId !== null ? FLUSH_DELAY_MS + 1 : 0;
  setTimeout(cb, delay);
};

export const scheduleSearchParamsFlush = (
  getSetSearchParams: () => SetURLSearchParams
): void => {
  readSetSearchParams = getSetSearchParams;

  if (timeoutId !== null) return;

  timeoutId = setTimeout(() => {
    timeoutId = null;

    const setSearchParams = readSetSearchParams?.();
    readSetSearchParams = null;

    const drained = drainQueuedSearchParamWrites();
    const isEmpty = !Object.keys(drained.params).length && !drained.removed.length;

    if (!setSearchParams || isEmpty) return;

    setSearchParams(
      (prev) => mergeQueuedSearchParamWrites(prev, drained),
      drained.options
    );
  }, FLUSH_DELAY_MS);
};

/** Test-only. */
export const resetSearchParamWriteQueue = (): void => {
  QUEUE.length = 0;
  readSetSearchParams = null;

  if (timeoutId !== null) clearTimeout(timeoutId);
  timeoutId = null;
};
